import React, { useState } from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  Input,
  MenuItem,
  DialogActions,
} from '@mui/material';
import { useMutation } from '@apollo/client/react';
import { TERMINATE, CANCEL_TERMINATE, GET_LICENSE } from './LicenseInfo.graphql';
import { LicenseModel } from '../../interfaces';
import { GET_SUBSCRIPTIONS } from './UsersInLicense.graphql';

interface TerminationProps {
  licenseId: number;
  license: LicenseModel;
  suspendValue: boolean;
}

export function TerminateButton({ license, licenseId, suspendValue }: TerminationProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [terminate, { loading }] = useMutation<any, any>(TERMINATE);
  const [cancelTermination] = useMutation<any, any>(CANCEL_TERMINATE, { variables: { input: { id: licenseId } } });
  const close = () => {
    setShowDialog(false);
    setReason(undefined);
  };
  const commit = (effectuateNow?: boolean) => {
    terminate({
      variables: { input: { id: licenseId, reason, effectuateNow } },
      update(cache) {
        if (!effectuateNow) return;

        const data = cache.readQuery<{ license: any }>({ query: GET_LICENSE, variables: { id: licenseId } });
        if (data)
          cache.writeQuery({
            query: GET_LICENSE,
            variables: { id: licenseId },
            data: {
              license: {
                __typename: 'LicenseQueryModel',
                isInvoicable: data.license.isInvoicable,
                bksLicense: { ...data.license.bksLicense, licenseStatus: 'Deleted', isActive: false },
                license: { ...data.license.license, pendingTermination: false, terminationReason: reason },
              },
            },
          });

        const subData = cache.readQuery<{ paidSubscriptions: any[]; subscriptions: any[]; license: any }>({
          query: GET_SUBSCRIPTIONS,
          variables: { id: licenseId },
        });
        if (subData)
          cache.writeQuery({
            query: GET_SUBSCRIPTIONS,
            variables: { id: licenseId },
            data: {
              subscriptions: [],
              paidSubscriptions: subData.paidSubscriptions,
              license: subData.license,
            },
          });
      },
    });
    close();
  };
  return (
    <Grid>
      {license && license.pendingTermination ? (
        <Button variant="contained" color="secondary" onClick={() => cancelTermination()}>
          Avbryt oppsigelse
        </Button>
      ) : (
        <>
          <Button variant="contained" color="secondary" onClick={() => setShowDialog(true)}>
            Avslutt kundeforhold
          </Button>
          <Dialog open={showDialog} onClose={close} aria-labelledby="form-dialog-title" fullWidth maxWidth="sm">
            <DialogTitle id="form-dialog-title">Avslutt kundeforhold</DialogTitle>
            <DialogContent>
              <DialogContentText>Oppgi årsak til oppsigelse:</DialogContentText>
              <form>
                <FormControl
                  sx={{
                    margin: (theme) => theme.spacing(),
                    minWidth: 120,
                  }}>
                  <InputLabel htmlFor="reason">Årsak</InputLabel>
                  <Select value={reason ? reason : ''} onChange={(ev) => setReason(ev.target.value as string)} input={<Input />}>
                    <MenuItem value="Ønsker ikke å oppgi årsak">
                      <em>Ønsker ikke å oppgi årsak</em>
                    </MenuItem>
                    <MenuItem value={'Konkurs/Opphørt'}>Konkurs/Opphørt</MenuItem>
                    <MenuItem value={'Pensjonist'}>Pensjonist</MenuItem>
                    <MenuItem value={'Har ikke bruk for (nytteverdi)'}>Har ikke bruk for (nytteverdi)</MenuItem>
                    <MenuItem value={'For høy pris'}>For høy pris</MenuItem>
                    <MenuItem value={'Har/Får tilgang gjennom andre'}>Har/Får tilgang gjennom andre</MenuItem>
                    <MenuItem value={'Endring av virksomhet/fagfelt'}>Endring av virksomhet/fagfelt</MenuItem>
                    <MenuItem value={'Diverse'}>Diverse</MenuItem>
                    <MenuItem value={'Mislighold'}>Mislighold</MenuItem>
                  </Select>
                </FormControl>
              </form>
            </DialogContent>
            <DialogActions>
              <Button onClick={close} color="primary">
                Avbryt
              </Button>
              <Button disabled={loading} onClick={() => commit()} variant="contained" color="primary">
                Avslutt
              </Button>
              <Button
                disabled={loading}
                onClick={() => commit(true)}
                variant="contained"
                color="primary"
                sx={{
                  background: (theme) => theme.palette.error.main,
                  '&:hover, &:focus': {
                    backgroundColor: (theme) => theme.palette.error.dark,
                  },
                }}
                title="Dette vil avslutte lisensen med umiddelbar virkning">
                Avslutt nå
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Grid>
  );
}
